import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  avaliacoesApi,
  type Nota,
  type RelatorioAvaliacoes,
} from '@/features/pos-venda/api/avaliacoesApi';
import { HttpError } from '@/shared/api/httpClient';
import {
  FILTROS_VAZIOS,
  normalizarLimite,
  type FiltrosTabelaAvaliacoes,
} from '@/features/relatorios/components/avaliacoesRelatorio';

const ATRASO_BUSCA_MS = 350;

export type ErroAvaliacoes = {
  mensagem: string;
  /** Sem internet / servidor fora — as avaliações só existem no servidor. */
  semConexao: boolean;
};

function traduzirErro(e: unknown): ErroAvaliacoes {
  if (e instanceof HttpError) {
    const semConexao = e.status === 0 || e.status === 503 || e.payload?.tipo === 'Indisponivel';
    return { mensagem: e.message, semConexao };
  }
  return { mensagem: e instanceof Error ? e.message : 'Erro ao carregar avaliações.', semConexao: false };
}

/**
 * Estado do relatório de avaliações: filtros da tabela, busca com
 * debounce, carregamento e o critério "mal avaliado". Fica na página
 * (e não no componente) porque o CSV/impressão precisam dos mesmos dados.
 * Só busca quando `habilitado` — a aba não custa nada enquanto fechada.
 */
export function useRelatorioAvaliacoes(opts: { inicio: string; fim: string; habilitado: boolean }) {
  const { inicio, fim, habilitado } = opts;
  const [filtros, setFiltros] = useState<FiltrosTabelaAvaliacoes>(FILTROS_VAZIOS);
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [dados, setDados] = useState<RelatorioAvaliacoes | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<ErroAvaliacoes | null>(null);
  const [salvandoLimite, setSalvandoLimite] = useState(false);
  // Responsáveis já vistos: com o filtro de responsável aplicado o
  // servidor devolve só ele em `porResponsavel`, e o select não pode
  // encolher para uma opção só.
  const [responsaveisVistos, setResponsaveisVistos] = useState<Map<string, string>>(new Map());
  const [versao, setVersao] = useState(0);
  const ultimaReq = useRef(0);

  // Debounce da busca livre.
  useEffect(() => {
    const id = setTimeout(() => setBuscaAplicada(filtros.busca.trim()), ATRASO_BUSCA_MS);
    return () => clearTimeout(id);
  }, [filtros.busca]);

  const chaveNotas = filtros.notas.join(',');

  useEffect(() => {
    if (!habilitado) return;
    const req = ++ultimaReq.current;
    setCarregando(true);
    setErro(null);
    avaliacoesApi
      .relatorio({
        inicio,
        fim,
        notas: chaveNotas ? (chaveNotas.split(',').map(Number) as Nota[]) : undefined,
        classificacao: filtros.classificacao,
        comComentario: filtros.comComentario || undefined,
        responsavelId: filtros.responsavelId || undefined,
        busca: buscaAplicada || undefined,
      })
      .then((r) => {
        if (req !== ultimaReq.current) return;
        setDados(r);
        setResponsaveisVistos((anterior) => {
          const novo = new Map(anterior);
          for (const p of r.porResponsavel) if (p.responsavelId) novo.set(p.responsavelId, p.nome);
          for (const i of r.itens) {
            if (i.responsavelId && i.responsavelNome) novo.set(i.responsavelId, i.responsavelNome);
          }
          return novo.size === anterior.size ? anterior : novo;
        });
      })
      .catch((e: unknown) => {
        if (req !== ultimaReq.current) return;
        setErro(traduzirErro(e));
      })
      .finally(() => {
        if (req === ultimaReq.current) setCarregando(false);
      });
  }, [
    habilitado,
    inicio,
    fim,
    chaveNotas,
    filtros.classificacao,
    filtros.comComentario,
    filtros.responsavelId,
    buscaAplicada,
    versao,
  ]);

  const recarregar = useCallback(() => setVersao((v) => v + 1), []);

  const alterarFiltros = useCallback((parcial: Partial<FiltrosTabelaAvaliacoes>) => {
    setFiltros((f) => ({ ...f, ...parcial }));
  }, []);

  const limparFiltros = useCallback(() => {
    setFiltros(FILTROS_VAZIOS);
    setBuscaAplicada('');
  }, []);

  /** Salva o critério e recarrega. Devolve a mensagem de erro, se houver. */
  const salvarLimite = useCallback(
    async (limite: number): Promise<string | null> => {
      setSalvandoLimite(true);
      try {
        const r = await avaliacoesApi.salvarLimite(limite);
        setDados((d) => (d ? { ...d, limiteRuim: normalizarLimite(r?.limiteRuim ?? limite) } : d));
        recarregar();
        return null;
      } catch (e) {
        return traduzirErro(e).mensagem;
      } finally {
        setSalvandoLimite(false);
      }
    },
    [recarregar],
  );

  const responsaveis = useMemo(
    () =>
      Array.from(responsaveisVistos.entries())
        .map(([id, nome]) => ({ id, nome }))
        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [responsaveisVistos],
  );

  return {
    dados,
    carregando,
    erro,
    filtros,
    alterarFiltros,
    limparFiltros,
    recarregar,
    salvarLimite,
    salvandoLimite,
    responsaveis,
  };
}

export type EstadoRelatorioAvaliacoes = ReturnType<typeof useRelatorioAvaliacoes>;
